import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronLeft, ChevronRight, Globe, HelpCircle } from 'lucide-react';
import { resolveZoomTargetAtPoint } from '../../utils/zoomTarget';
import { YearView } from './views/YearView';
import { MonthView } from './views/MonthView';
import { WeekView } from './views/WeekView';
import { DayView } from './views/DayView';
import { useZoomCalendar, ZOOM_LEVEL_KEYS, stripTime, getWeekWindowStart } from '../../hooks/useZoomCalendar';
import { useHorizontalSwipeNavigate } from '../../hooks/useHorizontalSwipeNavigate';
import { useSocket } from '../../hooks/useSocket';
import { Availability } from '../../types';
import { getTimezoneName } from '../../utils/timezone';
import { getAppLocale } from '../../utils/locale';
import { isViewRangeFullyPast } from '../../utils/slotTime';

interface ZoomableCalendarProps {
  roomId: string;
  participantName: string;
  availability: Availability[];
  onRefresh?: () => void;
  browserFingerprint: string;
  onNameReassigned?: (name: string) => void;
  onRateLimited?: () => void;
}

function formatDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const reducedTransition = { duration: 0.12 };
const ZOOM_LOCK_MS = 300;
const HINTS_KEY = 'zoomCalHintsSeen';

const zoomEase = [0.22, 1, 0.36, 1] as const;

const zoomVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    scale: direction > 0 ? 0.97 : 1.02,
  }),
  center: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 1,
  },
};

const zoomTransition = {
  enter: { duration: 0.24, ease: zoomEase },
  center: { duration: 0.24, ease: zoomEase },
  exit: { duration: 0.1, ease: 'easeIn' as const },
};

export function ZoomableCalendar({
  roomId,
  participantName,
  availability,
  onRefresh,
  browserFingerprint,
  onNameReassigned,
  onRateLimited,
}: ZoomableCalendarProps) {
  const { t, i18n } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const [showHints, setShowHints] = useState(false);
  const [showTimezone, setShowTimezone] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(HINTS_KEY)) setShowHints(true);
    } catch {
      /* ignore */
    }
  }, []);
  const dismissHints = () => {
    setShowHints(false);
    try {
      localStorage.setItem(HINTS_KEY, '1');
    } catch {
      /* ignore */
    }
  };
  const {
    level,
    viewDate,
    zoomIn,
    zoomOut,
    jumpToLevel,
    navigate,
    goToToday,
  } = useZoomCalendar(1);

  const [isMobileView, setIsMobileView] = useState(false);
  const calRef = useRef<HTMLDivElement>(null);
  const busyZoom = useRef(false);
  const pinchLast = useRef<number | null>(null);
  const pinchAccum = useRef(0);
  const pinchCenter = useRef<{ x: number; y: number } | null>(null);
  const prevLevelRef = useRef(level);
  const [zoomDirection, setZoomDirection] = useState(1);
  const viewShellRef = useRef<HTMLDivElement>(null);
  const viewContentRef = useRef<HTMLDivElement>(null);
  const headerActionsRef = useRef<HTMLDivElement>(null);
  const [viewMinHeight, setViewMinHeight] = useState<number | undefined>();
  const [isSlotDrawMode, setIsSlotDrawMode] = useState(false);
  const isSlotDrawModeRef = useRef(isSlotDrawMode);
  isSlotDrawModeRef.current = isSlotDrawMode;

  useEffect(() => {
    if (level < 2) setIsSlotDrawMode(false);
  }, [level]);

  const syncViewHeight = useCallback(() => {
    const el = viewContentRef.current;
    if (!el) return;
    const next = Math.ceil(el.getBoundingClientRect().height);
    if (next > 0) setViewMinHeight(next);
  }, []);

  useEffect(() => {
    if (level === prevLevelRef.current) return;
    setZoomDirection(level > prevLevelRef.current ? 1 : -1);
    prevLevelRef.current = level;
  }, [level]);

  useEffect(() => {
    if (!showHints && !showTimezone) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (headerActionsRef.current && !headerActionsRef.current.contains(event.target as Node)) {
        setShowHints(false);
        setShowTimezone(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHints, showTimezone]);

  useEffect(() => {
    const check = () => setIsMobileView(window.innerWidth <= 500);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const weekStep = isMobileView ? 5 : 7;
  const weekWindowStart = useMemo(
    () => getWeekWindowStart(viewDate, weekStep),
    [viewDate, weekStep]
  );

  const handleNavigate = useCallback(
    (delta: number) => navigate(delta, { weekStepDays: weekStep }),
    [navigate, weekStep]
  );

  const handleDrawModeChange = useCallback((active: boolean) => {
    isSlotDrawModeRef.current = active;
    setIsSlotDrawMode(active);
  }, []);

  const swipeHandlers = useHorizontalSwipeNavigate(
    handleNavigate,
    () => !isSlotDrawModeRef.current
  );

  useLayoutEffect(() => {
    setViewMinHeight(undefined);
  }, [level]);

  useLayoutEffect(() => {
    const el = viewContentRef.current;
    if (!el) return;

    syncViewHeight();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(syncViewHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [level, viewDate, weekWindowStart, syncViewHeight]);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useSocket({
    roomId,
    participantName,
    onAvailabilityUpdated: useCallback(() => {
      onRefreshRef.current?.();
    }, []),
  });

  const lockZoom = useCallback(() => {
    busyZoom.current = true;
    window.setTimeout(() => {
      busyZoom.current = false;
    }, ZOOM_LOCK_MS);
  }, []);

  const doZoomIn = useCallback(
    (targetDate?: Date, coords?: { x: number; y: number }) => {
      if (busyZoom.current || level >= 3) return;
      lockZoom();
      const fromPoint = coords ? resolveZoomTargetAtPoint(coords.x, coords.y) : null;
      zoomIn(fromPoint ?? targetDate);
    },
    [level, lockZoom, zoomIn]
  );

  const doZoomOut = useCallback(() => {
    if (busyZoom.current || level <= 0) return;
    lockZoom();
    zoomOut();
  }, [level, lockZoom, zoomOut]);

  useEffect(() => {
    const root = calRef.current;
    if (!root) return;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;

      e.preventDefault();
      if (e.deltaY < 0) doZoomIn(undefined, { x: e.clientX, y: e.clientY });
      else if (e.deltaY > 0) doZoomOut();
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [doZoomIn, doZoomOut]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === '+' || e.key === '=') doZoomIn();
      if (e.key === '-') doZoomOut();
      if (e.key === 'ArrowLeft') handleNavigate(-1);
      if (e.key === 'ArrowRight') handleNavigate(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [doZoomIn, doZoomOut, handleNavigate]);

  useEffect(() => {
    const root = calRef.current;
    if (!root) return;

    const pinchDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const pinchMidpoint = (touches: TouchList) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    const touchOnCalendar = (touches: TouchList) => {
      for (let i = 0; i < touches.length; i++) {
        const hit = document.elementFromPoint(touches[i].clientX, touches[i].clientY);
        if (hit instanceof Node && root.contains(hit)) return true;
      }
      return false;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (isSlotDrawModeRef.current) return;
      if (e.touches.length === 2 && touchOnCalendar(e.touches)) {
        e.preventDefault();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isSlotDrawModeRef.current) return;
      if (e.touches.length !== 2 || !touchOnCalendar(e.touches)) return;

      e.preventDefault();

      const dist = pinchDistance(e.touches);
      if (pinchLast.current === null) {
        pinchLast.current = dist;
        pinchAccum.current = 0;
        pinchCenter.current = pinchMidpoint(e.touches);
        return;
      }

      pinchAccum.current += dist - pinchLast.current;
      pinchLast.current = dist;
      pinchCenter.current = pinchMidpoint(e.touches);

      if (pinchAccum.current > 30 && !busyZoom.current) {
        pinchAccum.current = 0;
        const center = pinchCenter.current;
        doZoomIn(undefined, center ?? undefined);
      } else if (pinchAccum.current < -30 && !busyZoom.current) {
        pinchAccum.current = 0;
        doZoomOut();
      }
    };

    const resetPinch = () => {
      pinchLast.current = null;
      pinchAccum.current = 0;
      pinchCenter.current = null;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) resetPinch();
    };

    root.addEventListener('touchstart', onTouchStart, { passive: false });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', onTouchEnd, { passive: true });
    root.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [doZoomIn, doZoomOut]);

  const locale = getAppLocale(i18n.language);

  const formatTitle = () => {
    if (level === 0) return String(viewDate.getFullYear());
    if (level === 1) {
      return viewDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    if (level === 2) {
      const start = new Date(weekWindowStart);
      const end = new Date(weekWindowStart);
      end.setDate(end.getDate() + weekStep - 1);
      const a = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      const b = end.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
      return `${a} – ${b}`;
    }
    return viewDate.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleJumpLevel = (target: 0 | 1 | 2 | 3) => {
    if (target === level) return;
    lockZoom();
    jumpToLevel(target);
  };

  const dayDateStr = formatDayKey(viewDate);

  const isRangeFullyPast = useMemo(
    () => isViewRangeFullyPast(level, viewDate, weekWindowStart, weekStep, dayDateStr),
    [level, viewDate, weekWindowStart, weekStep, dayDateStr]
  );

  const viewKey = String(level);

  return (
    <div ref={calRef} className="room-calendar mx-auto w-full max-w-[820px] select-none px-1 pb-4 sm:px-2" style={{ touchAction: 'pan-x pan-y' }}>
      <div className="mb-3 flex flex-wrap items-center gap-0.5 text-[10px] tracking-wide text-room-text-faint">
        {ZOOM_LEVEL_KEYS.map((key, i) => (
          <span key={key} className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => handleJumpLevel(i as 0 | 1 | 2 | 3)}
              className={`rounded-full px-2 py-px transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent ${
                level === i
                  ? 'bg-room-surface-2/80 font-medium text-room-text-dim'
                  : 'text-room-text-faint/70 hover:text-room-text-dim'
              }`}
            >
              {t(`calendar.zoom.${key}`)}
            </button>
            {i < 3 ? <span className="px-px text-[9px] text-room-text-faint/50">›</span> : null}
          </span>
        ))}
        <div ref={headerActionsRef} className="relative ml-auto flex items-center gap-0.5">
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowTimezone(s => !s);
                setShowHints(false);
              }}
              aria-expanded={showTimezone}
              aria-label={`${t('room.timezoneInfo')} (${getTimezoneName()})`}
              className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent ${
                showTimezone
                  ? 'bg-room-surface-2 text-room-text-dim'
                  : 'text-room-text-faint hover:bg-room-surface-2 hover:text-room-text-dim'
              }`}
            >
              <Globe size={13} />
            </button>
            {showTimezone ? (
              <div className="absolute right-0 top-7 z-30 w-[240px] rounded-lg border border-room-border bg-room-surface p-3 shadow-xl">
                <p className="flex items-start gap-2 text-[10px] leading-relaxed text-room-text-dim">
                  <Globe size={13} className="mt-0.5 shrink-0 text-room-text-faint" aria-hidden />
                  <span>
                    {t('room.timezoneInfo')} ({getTimezoneName()})
                  </span>
                </p>
              </div>
            ) : null}
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowHints(s => !s);
                setShowTimezone(false);
              }}
              aria-expanded={showHints}
              aria-label={t('room.calendarTips')}
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent ${
                showHints
                  ? 'bg-room-surface-2 text-room-text-dim'
                  : 'text-room-text-faint hover:bg-room-surface-2 hover:text-room-text-dim'
              }`}
            >
              <HelpCircle size={13} />
              <span className="text-[10px] font-medium">{t('room.calendarTips')}</span>
            </button>
            {showHints ? (
              <div className="absolute right-0 top-7 z-30 w-[220px] rounded-lg border border-room-border bg-room-surface p-3 shadow-xl">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-room-text">{t('room.calendarTips')}</span>
                  <button
                    type="button"
                    onClick={dismissHints}
                    className="text-[10px] text-room-text-dim hover:text-room-text"
                  >
                    {t('calendar.hints.gotIt')}
                  </button>
                </div>
                <div className="flex flex-col gap-1.5 text-[10px] text-room-text-dim">
                  <span>{t('calendar.hints.scrollIn')}</span>
                  <span>{t('calendar.hints.pinchOut')}</span>
                  <span>{t('calendar.hints.clickDate')}</span>
                  <span>{t('calendar.hints.navigatePaint')}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mb-5 flex items-center justify-between gap-2">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => handleNavigate(-1)}
            disabled={isSlotDrawMode}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-room-border bg-transparent text-room-text-dim transition-colors hover:bg-room-surface-2 disabled:pointer-events-none disabled:opacity-40"
            aria-label={t('calendar.nav.previous')}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => handleNavigate(1)}
            disabled={isSlotDrawMode}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-room-border bg-transparent text-room-text-dim transition-colors hover:bg-room-surface-2 disabled:pointer-events-none disabled:opacity-40"
            aria-label={t('calendar.nav.next')}
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => level > 0 && doZoomOut()}
          title={level > 0 ? t('calendar.zoom.zoomOut') : undefined}
          className={`min-w-0 flex-1 px-2 text-center text-[1.375rem] font-medium leading-tight tracking-tight text-room-text ${
            level > 0 ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          {formatTitle()}
        </button>
        <button
          type="button"
          onClick={goToToday}
          className="h-8 shrink-0 rounded-md border border-room-border bg-transparent px-2.5 text-[11px] font-medium tracking-wide text-room-text-dim transition-colors hover:bg-room-surface-2"
        >
          {t('common.today')}
        </button>
      </div>

      {isRangeFullyPast ? (
        <div className="mb-4 flex justify-center px-1">
          <div
            role="status"
            className="w-full max-w-md rounded-xl border border-room-border bg-room-surface-2 px-3 py-2.5 text-center text-[11px] leading-relaxed text-room-text-dim shadow-sm"
          >
            {t('calendar.pastRangeNotice')}
          </div>
        </div>
      ) : null}

      <div
        ref={viewShellRef}
        className="relative overflow-hidden"
        style={viewMinHeight ? { minHeight: viewMinHeight } : undefined}
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
      >
        <AnimatePresence initial={false} custom={zoomDirection} mode="wait">
          <motion.div
            ref={viewContentRef}
            key={viewKey}
            custom={zoomDirection}
            variants={prefersReducedMotion ? undefined : zoomVariants}
            initial={prefersReducedMotion ? { opacity: 0 } : 'enter'}
            animate={prefersReducedMotion ? { opacity: 1 } : 'center'}
            exit={prefersReducedMotion ? { opacity: 0 } : 'exit'}
            transition={prefersReducedMotion ? reducedTransition : zoomTransition}
            onAnimationComplete={syncViewHeight}
            className="w-full min-w-0"
            style={{ transformOrigin: 'center top' }}
          >
            {level >= 2 ? (
              <>
                <div className="mb-6 hidden sm:block">
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-xl font-bold text-room-text">{t('room.markAvailability')}</h2>
                    <Calendar size={18} className="text-room-text-faint" />
                  </div>
                </div>
                <p className="mb-4 hidden text-sm text-room-text-dim sm:block">{t('room.instructions')}</p>
              </>
            ) : null}

          {level === 0 ? (
            <YearView
              viewDate={viewDate}
              availability={availability}
              onSelectMonth={(year, monthIndex) => {
                lockZoom();
                zoomIn(new Date(year, monthIndex, 1));
              }}
            />
          ) : null}
          {level === 1 ? (
            <MonthView
              viewDate={viewDate}
              availability={availability}
              onSelectDay={date => {
                lockZoom();
                zoomIn(stripTime(date));
              }}
            />
          ) : null}
          {level === 2 ? (
            <WeekView
              roomId={roomId}
              participantName={participantName}
              availability={availability}
              weekStartDate={weekWindowStart}
              visibleDayCount={weekStep}
              browserFingerprint={browserFingerprint}
              onNameReassigned={onNameReassigned}
              onRateLimited={onRateLimited}
              onDayHeaderClick={d => {
                const [yy, mm, dd] = d.split('-').map(Number);
                lockZoom();
                zoomIn(new Date(yy, mm - 1, dd));
              }}
              onDrawModeChange={handleDrawModeChange}
            />
          ) : null}
          {level === 3 ? (
            <DayView
              roomId={roomId}
              participantName={participantName}
              availability={availability}
              dayDate={dayDateStr}
              weekStartDate={weekWindowStart}
              visibleDayCount={weekStep}
              browserFingerprint={browserFingerprint}
              onNameReassigned={onNameReassigned}
              onRateLimited={onRateLimited}
              onDrawModeChange={handleDrawModeChange}
            />
          ) : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
